import { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, Clock, MapPin, CalendarPlus } from 'lucide-react';
import { buildIcs, formatDate, formatTime } from '../../lib/dates';
import { Modal } from '../../components/ui/Modal';
import type { Event } from '../../types';
import type { SectionProps } from './useSectionQuery';
import { SectionShell } from './SectionShell';

function icsFileName(title: string): string {
    return `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event'}.ics`;
}

/**
 * Only there when something is coming up. The Worker keeps an event in the
 * list until the end of its day, so it drops off by itself the day after. A
 * failed load hides the block too: an empty calendar is not worth the space,
 * and nothing here is urgent.
 */
export function EventsSection(props: SectionProps<Event[]>) {
    const { data } = props;
    // The dashboard asks for five upcoming events; this block shows three.
    const events = (data ?? []).slice(0, 3);
    const [selected, setSelected] = useState<Event | null>(null);
    const [icsUrl, setIcsUrl] = useState<string | null>(null);

    // Build the .ics blob URL when an event is opened; revoke it on close.
    useEffect(() => {
        if (!selected) return;
        const url = buildIcs({ title: selected.title, description: selected.description, location: selected.location, start: selected.event_date });
        // Deferred so React doesn't see a synchronous set-state inside the effect body.
        const id = window.setTimeout(() => setIcsUrl(url), 0);
        return () => {
            window.clearTimeout(id);
            URL.revokeObjectURL(url);
            setIcsUrl(null);
        };
    }, [selected]);

    if (events.length === 0) return null;

    return (
        <>
            <SectionShell title="Upcoming" icon={<CalendarIcon size={20} aria-hidden="true" />}>
                <div className="events-list">
                    {events.map((ev) => {
                        const date = new Date(ev.event_date);
                        return (
                            <button
                                key={ev.id}
                                type="button"
                                className="card event-card clickable"
                                onClick={() => setSelected(ev)}
                            >
                                <div className="event-date" aria-hidden="true">
                                    <span className="event-day">{date.getDate()}</span>
                                    <span className="event-month">{date.toLocaleString('en-ZA', { month: 'short' })}</span>
                                </div>
                                <div className="event-details">
                                    <strong>{ev.title}</strong>
                                    <p>{formatTime(ev.event_date)}{ev.location ? ` • ${ev.location}` : ''}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </SectionShell>

            <Modal
                open={!!selected}
                onClose={() => setSelected(null)}
                title="Event details"
                className="event-modal"
                footer={selected && (
                    <>
                        <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Close</button>
                        {icsUrl && (
                            <a href={icsUrl} download={icsFileName(selected.title)} className="btn btn-primary">
                                <CalendarPlus size={18} aria-hidden="true" /> Add to calendar
                            </a>
                        )}
                    </>
                )}
            >
                {selected && (
                    <>
                        <div className="event-detail-header">
                            <div className="event-date-large" aria-hidden="true">
                                <span className="event-day-large">{new Date(selected.event_date).getDate()}</span>
                                <span className="event-month-large">{new Date(selected.event_date).toLocaleString('en-ZA', { month: 'short' })}</span>
                            </div>
                            <div>
                                <h3>{selected.title}</h3>
                                <div className="event-meta-row">
                                    <Clock size={16} aria-hidden="true" />
                                    <span>{formatDate(selected.event_date, { weekday: 'long', day: 'numeric', month: 'long' })} at {formatTime(selected.event_date)}</span>
                                </div>
                                {selected.location && (
                                    <div className="event-meta-row">
                                        <MapPin size={16} aria-hidden="true" />
                                        <span>{selected.location}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {selected.description ? (
                            <div className="event-description"><p>{selected.description}</p></div>
                        ) : (
                            <p className="text-muted">No further details yet.</p>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
}
